import { useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { LayoutRow, LayoutColumn, ColumnWidth, ColumnMobileConfig, ColumnMargin, LayoutSectionConfig } from "./types";
import { useLayoutState } from "./useLayoutState";
import { LayoutRowComponent } from "./LayoutRow";
import { RowPresetPicker } from "./RowPresetPicker";
import { ColumnSettingsModal } from "./ColumnSettingsModal";
import { RowSettingsModal } from "./RowSettingsModal";

interface LayoutEditorProps {
  layoutState: ReturnType<typeof useLayoutState>;
  onAddSection: (rowId: string, columnId: string) => void;
  onEditSection: (rowId: string, columnId: string, sectionIndex: number) => void;
}

export const LayoutEditor = ({ layoutState, onAddSection, onEditSection }: LayoutEditorProps) => {
  const {
    rows,
    addRow,
    deleteRow,
    moveRow,
    updateRowSettings,
    addColumn,
    deleteColumn,
    updateColumnWidth,
    updateColumnMobileConfig,
    updateColumnMargin,
    deleteSection,
    onDragEnd,
  } = layoutState;

  // Column settings modal state
  const [columnSettingsTarget, setColumnSettingsTarget] = useState<{
    rowId: string;
    column: LayoutColumn;
  } | null>(null);

  // Row settings modal state
  const [rowSettingsTarget, setRowSettingsTarget] = useState<LayoutRow | null>(null);

  const handleOpenColumnSettings = (rowId: string, columnId: string) => {
    const row = rows.find((r) => r.id === rowId);
    const column = row?.columns.find((c) => c.id === columnId);
    if (column) {
      setColumnSettingsTarget({ rowId, column });
    }
  };

  const handleSaveColumnSettings = (width: ColumnWidth, mobileConfig: ColumnMobileConfig, margin: ColumnMargin) => {
    if (!columnSettingsTarget) return;
    updateColumnWidth(columnSettingsTarget.rowId, columnSettingsTarget.column.id, width);
    updateColumnMobileConfig(columnSettingsTarget.rowId, columnSettingsTarget.column.id, mobileConfig);
    updateColumnMargin(columnSettingsTarget.rowId, columnSettingsTarget.column.id, margin);
  };

  const handleSaveRowSettings = (gap: number, marginTop: number, marginBottom: number) => {
    if (!rowSettingsTarget) return;
    updateRowSettings(rowSettingsTarget.id, { gap, marginTop, marginBottom });
  };

  return (
    <div className="space-y-3">
      <DragDropContext onDragEnd={onDragEnd}>
        {rows.map((row, index) => (
          <LayoutRowComponent
            key={row.id}
            row={row}
            rowIndex={index}
            totalRows={rows.length}
            onMoveRow={(dir) => moveRow(row.id, dir)}
            onDeleteRow={() => deleteRow(row.id)}
            onOpenRowSettings={() => setRowSettingsTarget(row)}
            onAddColumn={() => addColumn(row.id)}
            onDeleteColumn={(columnId) => deleteColumn(row.id, columnId)}
            onOpenColumnSettings={(columnId) => handleOpenColumnSettings(row.id, columnId)}
            onAddSection={(columnId) => onAddSection(row.id, columnId)}
            onEditSection={(columnId, sectionIndex) => onEditSection(row.id, columnId, sectionIndex)}
            onDeleteSection={(columnId, sectionIndex) => deleteSection(row.id, columnId, sectionIndex)}
          />
        ))}
      </DragDropContext>

      {/* Add row area */}
      <div className="rounded-lg border-2 border-dashed border-slate-300 p-4 dark:border-slate-600">
        <p className="mb-3 text-sm font-medium text-slate-600 dark:text-slate-400">
          Dodaj wiersz:
        </p>
        <RowPresetPicker onSelect={(presetIndex) => addRow(presetIndex)} />
      </div>

      {/* Column Settings Modal */}
      <ColumnSettingsModal
        isOpen={!!columnSettingsTarget}
        onClose={() => setColumnSettingsTarget(null)}
        column={columnSettingsTarget?.column ?? null}
        onSave={handleSaveColumnSettings}
      />

      {/* Row Settings Modal */}
      <RowSettingsModal
        isOpen={!!rowSettingsTarget}
        onClose={() => setRowSettingsTarget(null)}
        gap={rowSettingsTarget?.gap ?? 24}
        marginTop={rowSettingsTarget?.marginTop ?? 0}
        marginBottom={rowSettingsTarget?.marginBottom ?? 0}
        onSave={handleSaveRowSettings}
      />
    </div>
  );
};
